import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { getCheckoutSessionStatus } from '../services/api';

type Status = 'loading' | 'success' | 'error';

export default function CheckoutReturnPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('loading');
  const [customerEmail, setCustomerEmail] = useState<string | null>(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');

    if (!sessionId) {
      setStatus('error');
      return;
    }

    async function checkStatus() {
      try {
        const result = await getCheckoutSessionStatus(sessionId!);

        if (result.status === 'complete') {
          setStatus('success');
          setCustomerEmail(result.customerEmail || null);

          // Redirect to billing page after a delay
          setTimeout(() => {
            navigate('/billing?success=true');
          }, 3000);
        } else {
          setStatus('error');
        }
      } catch (err) {
        console.error('Error checking session status:', err);
        setStatus('error');
      }
    }

    checkStatus();
  }, [searchParams, navigate]);

  if (status === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        <p className="mt-4 text-gray-600">Verifying your payment...</p>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <CheckCircleIcon className="h-16 w-16 text-green-500" />
        <h2 className="mt-4 text-2xl font-bold text-gray-900">Payment Successful!</h2>
        {customerEmail && (
          <p className="mt-2 text-gray-600">
            A confirmation email has been sent to {customerEmail}.
          </p>
        )}
        <p className="mt-2 text-sm text-gray-500">Redirecting to billing page...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh]">
      <XCircleIcon className="h-16 w-16 text-red-500" />
      <h2 className="mt-4 text-2xl font-bold text-gray-900">Something went wrong</h2>
      <p className="mt-2 text-gray-600">
        Your payment could not be verified. Please try again or contact support.
      </p>
      <button
        onClick={() => navigate('/billing')}
        className="mt-4 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500"
      >
        Return to Billing
      </button>
    </div>
  );
}
